import { NgClass } from '@angular/common';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltip } from '@angular/material/tooltip';
import { IncidentRuleStatus } from '@api/devices';
import { RestrictedElementDirective } from '@auth';
import { APPLICATION_TYPE, FeatureFlagPipe } from '@core';
import { formatIncidentRuleDuration } from '@devices';
import { type IncidentRuleCommon } from '@devices/models';
import { IncidentSeverityIconComponent } from '@incidents';

import { ScheduleInfoComponent } from '../schedule-info';

@Component({
  imports: [MatSlideToggleModule, MatTooltip, RestrictedElementDirective, IncidentSeverityIconComponent, MatIconModule, FeatureFlagPipe, NgClass, ScheduleInfoComponent],
  selector: 'avs-incident-rule',
  styleUrl: './incident-rule.component.scss',
  templateUrl: './incident-rule.component.html',
})
export class IncidentRuleComponent {
  showToggle = input(true);
  incidentRule = input.required<IncidentRuleCommon>();
  editable = input(true);
  measurementUnit = input<{ display: string; displayShort: string; } | null>();

  toggle = output<{ enabled: boolean; revert: () => void; rule: IncidentRuleCommon; }>();
  edit = output<IncidentRuleCommon>();
  delete = output<IncidentRuleCommon>();
  share = output<IncidentRuleCommon>();

  protected readonly eIncidentRuleStatus = IncidentRuleStatus;
  protected checked = signal(false);
  protected isMobile = inject(APPLICATION_TYPE) === 'mobile';
  protected durationLabel = computed(() => formatIncidentRuleDuration(this.incidentRule().pendingPeriodSeconds));
  protected durationMinutesLabel = computed(() => {
    const seconds = this.incidentRule().pendingPeriodSeconds;

    return seconds ? `${Math.round(seconds / 60)} min` : null;
  });

  constructor() {
    effect(() => this.checked.set(this.incidentRule().status === IncidentRuleStatus.ACTIVE));
  }

  protected onToggle(enabled: boolean) {
    this.checked.set(enabled);
    this.toggle.emit({
      enabled,
      revert: () => this.checked.set(!enabled),
      rule: this.incidentRule(),
    });
  }

  protected onEdit() {
    this.edit.emit(this.incidentRule());
  }

  protected onDelete() {
    this.delete.emit(this.incidentRule());
  }

  protected onShare() {
    this.share.emit(this.incidentRule());
  }
}
